import { html, css, LitElement, nothing } from 'lit';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { createPopper } from '@popperjs/core';
import AabIcon from '@aab/sc-aab-icon/dist/AabIcon';
import AabStatus from '@aab/sc-aab-status/dist/AabStatus';
import AabInput from '@aab/sc-aab-input/dist/AabInput';
import { dedupeMixin } from '@open-wc/dedupe-mixin';
import { sy_arrow_chevron_down, sy_tools_search } from '@aab/sc-aab-icon-set';
import { n100, white, g300 } from '@aab/sc-styles-colors';
import { classMap } from 'lit/directives/class-map.js';
import { setTranslations, translate, TranslateMixin } from '@aab/sc-aab-translate';

/**
 * Converts strings and numbers (which may be passed as options as well), to
 * `Option` objects, so we can internally always work with a uniform array of
 * `Option` objects
 *
 * For robustness and flexibilty, a single string or number may also be passed
 * as the options. In this case, the argument will be 'wrapped' in an array,
 * converted as normal, and returned. (I.e., an argument like 'aString' will be
 * treated as ['aString'])
 *
 * @param {Array<object|string|number>|string|number} options
 * @returns {Option[]}
 */
function convertOptions(options) {
  return ([].concat(options)).map((option) => {
    if (typeof option === 'string' || typeof option === 'number') {
      return { name: option, value: option };
    }
    return option;
  });
}

/* eslint-disable class-methods-use-this */

const SPACE_BAR_KEY = ' ';
const TAB_KEY = 'Tab';
const ENTER_KEY = 'Enter';
const DOWN_ARROW_KEY = 'ArrowDown';
const UP_ARROW_KEY = 'ArrowUp';
const ESCAPE_KEY = 'Escape';
const HOME_KEY = 'Home';
const END_KEY = 'End';

var KeyboardDispatcherMixin = dedupeMixin(
  (superclass) => class extends superclass {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Dispatches keyboard events to the correct handlers
     */
    handleKeydown(event) {
      switch (event.key) {
        case ENTER_KEY:
          this.handleEnterKey(event);
          break;
        case SPACE_BAR_KEY:
          this.handleSpaceBarKey(event);
          break;
        case TAB_KEY:
          this.handleTabKey(event);
          break;
        case ESCAPE_KEY:
          this.handleEscapeKey(event);
          break;
        case UP_ARROW_KEY:
          this.handleUpArrowKey(event);
          break;
        case DOWN_ARROW_KEY:
          this.handleDownArrowKey(event);
          break;
        case HOME_KEY:
          this.handleHomeKey(event);
          break;
        case END_KEY:
          this.handleEndKey(event);
          break;
      }
    }

    handleEnterKey() { /* To be (optionally) overriden by a subclass */ }
    handleSpaceBarKey() { /* To be (optionally) overriden by a subclass */ }
    handleTabKey() { /* To be (optionally) overriden by a subclass */ }
    handleEscapeKey() { /* To be (optionally) overriden by a subclass */ }
    handleUpArrowKey() { /* To be (optionally) overriden by a subclass */ }
    handleDownArrowKey() { /* To be (optionally) overriden by a subclass */ }
    handleHomeKey() { /* To be (optionally) overriden by a subclass */ }
    handleEndKey() { /* To be (optionally) overriden by a subclass */ }
  },
);

/**
 * @typedef {import('../AabSelect.js').Option} Option
 */

/**
 * @typedef {import('../AabSelect.js').Group} Group
 */

/**
 * @typedef {object} SearchResult
 * @property {number} index
 * @property {?Option} option
 */

/**
 * @typedef {object} GroupedSearchResult
 * @property {number} groupIndex
 * @property {number} optionIndex
 * @property {?Option} option
 */

/**
 * Returns the 'next' non-disabled option (and it's index) from an array of
 * options
 *
 * @param {number} startIndex
 * @param {Option[]} options
 * @returns {SearchResult}
 */
function findNextOption(startIndex, options) {
  let index = startIndex;
  let option;

  do {
    index += 1;
    if (index > options.length - 1) {
      index = 0;
    }
    option = options[index];
  } while (option.disabled && index !== startIndex);

  return { index, option };
}

/**
 * Returns the 'next' non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {number} groupIndex
 * @param {number} optionIndex
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
function findNextGroupedOption(groupIndex, optionIndex, groups) {
  let i = groupIndex;
  let j = optionIndex;
  let option;

  do {
    do {
      j += 1;
      if (i >= groups.length - 1 && j > groups[i].options.length - 1) {
        i = 0;
        j = 0;
      }
      if (j > groups[i].options.length - 1) {
        i += 1;
        j = 0;
      }
      option = groups[i].options[j];
    } while ((option.disabled) && j !== optionIndex);
  } while ((!option));

  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}

/**
 * Returns the 'previous' non-disabled option (and it's index) from an array of
 * options
 *
 * @param {number} startIndex
 * @param {Option[]} options
 * @returns {SearchResult}
 */
function findPreviousOption(startIndex, options) {
  let index = startIndex;
  let option;

  do {
    index -= 1;
    if (index < 0) {
      index = options.length - 1;
    }
    option = options[index];
  } while ((option.disabled) && index !== startIndex);

  return { index, option };
}

/**
 * Returns the 'previous' non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {number} groupIndex
 * @param {number} optionIndex
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
function findPreviousGroupedOption(groupIndex, optionIndex, groups) {
  let i = groupIndex;
  let j = optionIndex;
  let option;

  do {
    do {
      j -= 1;
      if (i <= 0 && j < 0) {
        i = groups.length - 1;
        j = groups[i].options.length - 1;
      }
      if (j < 0) {
        i -= 1;
        j = groups[i].options.length - 1;
      }
      option = groups[i].options[j];
    } while (option.disabled && j !== optionIndex);
  } while ((!option));

  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}

/**
 * Returns the first non-disabled option (and it's index) from an array of
 * options
 *
 * @param {Option[]} options
 * @returns {SearchResult}
 */
function findFirstOption(options) {
  const index = options.findIndex((option) => !option.disabled);
  if (index < 0) {
    return { index, option: null };
  }
  return { index, option: options[index] };
}

/**
 * Returns the first non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
function findFirstGroupedOption(groups) {
  const [groupIndex, optionIndex] = groups.flatMap((group, currentGroupIndex) => {
    const foundOptionIndex = group.options.findIndex((option) => !option.disabled);
    return foundOptionIndex > -1 ? [currentGroupIndex, foundOptionIndex] : [-1, -1];
  });

  if (groupIndex < 0 || optionIndex < 0) {
    return { groupIndex, optionIndex, option: null };
  }
  return { groupIndex, optionIndex, option: groups[groupIndex].options[optionIndex] };
}

/**
 * Returns the last non-disabled option (and it's index) from an array of
 * options
 *
 * @param {Option[]} options
 * @returns {SearchResult}
 */
function findLastOption(options) {
  let i;
  let option;

  for (i = options.length - 1; i > -1; i -= 1) {
    option = options[i];
    if (!option.disabled) {
      break;
    }
  }
  if (i < 0) {
    return { index: i, option: null };
  }
  return { index: i, option: options[i] };
}

/**
 * Returns the last non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
function findLastGroupedOption(groups) {
  let i;
  let j;
  let option;

  for (i = 0; i <= groups.length - 1; i += 1) {
    for (j = groups[i].options.length - 1; j > -1; j -= 1) {
      option = groups[i].options[j];
      if (!option.disabled) {
        break;
      }
    }
    if (i === groups.length - 1) {
      break;
    }
  }
  if (j < 0) {
    return { groupIndex: -1, optionIndex: -1, option: null };
  }
  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}

/**
 * AabSelectButton is the 'toggle' part of the AabSelect; the area on which you click to open the dropdown, which displays the selected option,
 * and displays the chevron. According to [WAI ARIA guidelines](https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html),
 * it must be a <button>.
 *
 * @param {boolean} arg0.hasLabel whether or not the AabSelect has a label passed to it. Controls the ariaLabelledBy attributed
 * @param {boolean} arg0.hasValue whether or not the AabSelect has a set value. Determines whether to show the placeholder or not
 * @param {boolean} arg0.isOpen whether or not the dropdown of the AabSelect is open. Determines, among others, the direction of the chevron
 * @param {string} arg0.variant the variant style of the AabSelect, made visible through the borderColor of the AabSelectButton
 * @param {boolean} arg0.disabled whether the button should be disabled
 * @param {string} arg0.displayValue the displayValue of the AabSelectButton
 * @param {string} arg0.icon an svg string, which gets passed to an aab-icon
 * @param {function} arg0.onClick a callback for when the button has been clicked
 */
var AabSelectButton = ({
  hasLabel,
  hasValue,
  isOpen,
  variant,
  disabled,
  noBorder,
  displayValue,
  icon,
  onClick,
}) => html`
  <button
    aria-label="${!hasLabel ? 'Button' : ''}"
    aria-labelledby="${hasLabel ? 'aab-select-label' : ''} aab-select-target"
    aria-haspopup="listbox"
    aria-expanded="${isOpen ? 'true' : 'false'}"
    ?disabled="${disabled}"
    class=${`target ${variant ? `target--${variant}` : ''}
    ${disabled ? 'target--disabled' : ''}
    ${noBorder ? 'target--no-border' : ''}
    `}
    id="aab-select-target"
    @click=${onClick}
  >
    <span
      class="${`target__label ${!hasValue ? 'target__label--is-placeholder' : ''}`}"
    >
      ${icon && html`<aab-icon class="option-icon" size="3" svg="${icon}"></aab-icon>`}

      ${displayValue}
    </span>

    <span class=${`arrow ${isOpen ? 'arrow--flipped' : ''}`}>
      <aab-icon
        aria-hidden="true"
        class="d-flex"
        svg=${sy_arrow_chevron_down}
        size="3"
        color="${disabled ? n100 : ''}"
      ></aab-icon>
    </span>
  </button>
`;

/* eslint-disable lit-a11y/click-events-have-key-events */
/* eslint-disable lit-a11y/mouse-events-have-key-events */

/**
 * For WAI ARIA guidelines on roles, states, aria-attributes, etc., see:
 * https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html
 *
 * @param {string} arg0.id the id attribute of the option
 * @param {boolean} arg0.isSelected whether or not the option is selected; i.e., whether is be render in the 'selected' style
 * @param {boolean} arg0.isFocused whether or not the option is focused; i.e., whether is be render in the 'focused' style
 * @param {string} arg0.name the name of the option, i.e., what it's displayed label will be in a dropdown
 * @param {function} arg0.icon an svg string which gets passed to an aab-icon
 * @param {boolean} arg0.disabled whether or not the option is disabled
 * @param {function} arg0.onClick callback for when the option is clicked
 * @param {function} arg0.onMouseOver callback for when a mouseover event occurs on an option
 */

var AabSelectOption = ({
  id,
  isSelected,
  isFocused,
  name,
  icon,
  iconType,
  disabled,
  onClick,
  onMouseOver,
}) => html`
  <li
    id=${id}
    role="option"
    aria-selected="${isSelected ? 'true' : 'false'}"
    aria-label="${name}"
    class=${`option
      ${isSelected ? 'option--selected' : ''} ${disabled ? 'option--disabled' : ''} ${isFocused ? 'option--focused' : ''}
    `}
    @click=${() => !disabled && onClick()}
    @mouseover=${onMouseOver}
  >
  ${(iconType === 'system')
    ? html`${icon && html`<aab-icon class="option-icon" color="${isSelected ? white : g300}" size="3" svg=${icon}></aab-icon>`}
    
    ${name}`
    : html`${icon && html`<aab-icon class="option-icon" size="3" svg=${icon}></aab-icon>`}
      
    ${name}`
  }
  </li>
`;

var selectComponentNoResults$1 = "Geen resultaten gevonden";
var selectComponentAriaLabel$1 = "Zoeken";
var selectComponentErrorVariant$1 = "Foutief";
var selectComponentSuccessVariant$1 = "Succesvol";
var selectComponentWarningVariant$1 = "Waarschuwing";
var selectComponentInformationVariant$1 = "Informatie";
var nl = {
	selectComponentNoResults: selectComponentNoResults$1,
	selectComponentAriaLabel: selectComponentAriaLabel$1,
	selectComponentErrorVariant: selectComponentErrorVariant$1,
	selectComponentSuccessVariant: selectComponentSuccessVariant$1,
	selectComponentWarningVariant: selectComponentWarningVariant$1,
	selectComponentInformationVariant: selectComponentInformationVariant$1
};

var selectComponentNoResults = "No results found";
var selectComponentAriaLabel = "Search";
var selectComponentErrorVariant = "Error";
var selectComponentSuccessVariant = "Success";
var selectComponentWarningVariant = "Warning";
var selectComponentInformationVariant = "Information";
var en = {
	selectComponentNoResults: selectComponentNoResults,
	selectComponentAriaLabel: selectComponentAriaLabel,
	selectComponentErrorVariant: selectComponentErrorVariant,
	selectComponentSuccessVariant: selectComponentSuccessVariant,
	selectComponentWarningVariant: selectComponentWarningVariant,
	selectComponentInformationVariant: selectComponentInformationVariant
};

setTranslations({ nl, en });

/**
 * For WAI ARIA guidelines on roles, states, aria-attributes, etc., see:
 * https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html
 *
 * @param {string} arg0.isSmartSearch whether smartSearchText present or not
 * @param {boolean} arg0.onInput a callback for when a input event occurs on the input text box
 */
var AabSelectSmartSearch = ({ onInput, itemsLength }) => html` <div
    class="smart-search-container"
  >
    <aab-input icon-position="right">
      <input
        type="text"
        value=""
        name="smart-search-text"
        id="smart-search-text"
        class="smart-search-text"
        aria-label="${translate("selectComponentAriaLabel")}"
        @input=${(event) => onInput(event)}
      />
      <aab-icon slot="icon" size="3" svg="${sy_tools_search}"></aab-icon>
    </aab-input>
  </div>
  ${!itemsLength
    ? html` <p
        class="m-0 no-results"
        aria-label="${translate("selectComponentNoResults")}"
      >
        <slot name="no-results-text" class="m-0"
          >${translate("selectComponentNoResults")}</slot
        >
      </p>`
    : ""}`;

/* eslint-disable lit-a11y/mouse-events-have-key-events */

/**
 * For WAI ARIA guidelines on roles, states, aria-attributes, etc., see:
 * https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html
 *
 * @param {string} arg0.value the value of the AabSelect
 * @param {boolean} arg0.hasLabel whether or not the AabSelect has a label passed to it
 * @param {number} arg0.focusedOptionIndex the index of the 'focused' option
 * @param {boolean} arg0.isOpen whether the dropdown is open or not (i.e., visible or collapsed)
 * @param {string} arg0.width the width of the dropdown. Can be either fixed to the size of the button ('fixed'), or determined by its contents ('auto')
 * @param {string} arg0.align the horizonal alignment of the dropdown. For when the dropdown has a different size than it's AabSelect. 'left' or 'right'
 * @param {Array<Option>} arg0.options the options of the AabSelect
 * @param {function} arg0.onMouseOut a callback for when a mouseover event occurs on the dropdown
 * @param {function} arg0.onOptionClick a callback for when a click event occurs on one of the options
 * @param {function} arg0.onOptionMouseOver a callback for when a mouseover event occurs on one of the options
 * @param {boolean} arg0.isSmartSearch whether smartSearchText present or not
 */
var AabSelectDropdown = ({
  value,
  isSmartSearch,
  hasLabel,
  focusedOptionIndex,
  isOpen,
  width,
  align,
  options,
  onMouseOut,
  onOptionClick,
  onOptionMouseOver,
  onInputChange,
}) => { const classes = {
  'd-block': isOpen,
  'dropdown--align-right': align === 'right',
  'dropdown--align-left': align !== 'right',
  'dropdown--width-full': width === 'fixed',
  'dropdown--width-auto': width !== 'fixed',
};
  return html` <div
  class="dropdown ${classMap(classes)}"
>
  ${isSmartSearch
    ? AabSelectSmartSearch({
        onInput: onInputChange,
        itemsLength: options.length,
      })
    : null}
  <ul
    tabindex="-1"
    role="listbox"
    class="p-0 m-0"
    aria-labelledby="${hasLabel ? 'aab-select-label' : ''}"
    aria-label="${!hasLabel ? 'Dropdown' : ''}"
    aria-activedescendant="${focusedOptionIndex > -1
      ? `aab-option-${focusedOptionIndex}`
      : undefined}"
    @mouseout=${onMouseOut}
  >
    ${options.map((option, index) => {
      return AabSelectOption({
        id: `aab-option-${index}`,
        isSelected: option.value === value,
        isFocused: index === focusedOptionIndex,
        disabled: option.disabled,
        name: option.name,
        icon: option.icon,
        iconType: option.iconType,
        onClick: onOptionClick,
        onMouseOver: () => onOptionMouseOver(index),
      });
    }
    )}
  </ul>
</div>`};

/**
 * For WAI ARIA guidelines on roles, states, aria-attributes, etc., see:
 * https://www.w3.org/TR/2019/WD-wai-aria-practices-1.2-20191218/examples/listbox/listbox-grouped.html
 *
 * @param {number} arg0.index the index of the group, in the context of the AabSelect
 * @param {string} arg0.name the (displayed) name of the group
 * @param {Array<Option>} arg0.options the options of the group
 * @param {string} arg0.value the value of the AabSelect
 * @param {number} arg0.focusedGroupIndex the index of the group which contains the 'focused' option
 * @param {number} arg0.focusedOptionIndex the index of the 'focused' option
 * @param {function} arg0.onOptionClick a callback for when a click event occurs on an option
 * @param {function} arg0.onOptionMouseOver a callback for when a mouseover event occurs on an option
 */
var AabSelectGroup = ({
  index,
  name,
  options,
  value,
  focusedGroupIndex,
  focusedOptionIndex,
  onOptionClick,
  onOptionMouseOver,
}) => html`
  <ul role="group" aria-labelledby="aab-option-label-${index}" class="group">
    <li
      id="aab-select-group-label-${index}"
      class="group__label"
      role="presentation"
    >
      <label>${name}</label>
    </li>

    ${options.map((option, optionIndex) => AabSelectOption({
    id: `aab-option-${index}-${optionIndex}`,
    isSelected: option.value === value,
    isFocused: focusedOptionIndex === optionIndex && focusedGroupIndex === index,
    disabled: option.disabled,
    name: option.name,
    icon: option.icon,
    onClick: onOptionClick,
    onMouseOver: () => onOptionMouseOver(optionIndex),
  }))}
  </ul>
`;

/* eslint-disable lit-a11y/mouse-events-have-key-events */

/**
 * For WAI ARIA guidelines on roles, states, aria-attributes, etc., see:
 * https://www.w3.org/TR/2019/WD-wai-aria-practices-1.2-20191218/examples/listbox/listbox-grouped.html
 *
 * @param {string} arg0.value the value of the AabSelect
 * @param {boolean} arg0.hasLabel whether or not the AabSelect has a label passed to it
 * @param {number} arg0.focusedGroupIndex the index of the group which contains the 'focused' option
 * @param {number} arg0.focusedOptionIndex the index of the 'focused' option
 * @param {boolean} arg0.isOpen whether the dropdown is open or not (i.e., visible or collapsed)
 * @param {string} arg0.width the width of the dropdown. Can be either fixed to the size of the button ('fixed'), or determined by its contents ('auto')
 * @param {string} arg0.align the horizonal alignment of the dropdown. For when the dropdown has a different size than it's AabSelect. 'left' or 'right'
 * @param {Array<Group>} arg0.groups the groups of the AabSelect
 * @param {function} arg0.onMouseOut a callback for when a mouseover event occurs on the dropdown
 * @param {function} arg0.onOptionClick a callback for when a click event occurs on one of the options
 * @param {function} arg0.onOptionMouseOver a callback for when a mouseover event occurs on one of the options
 */
var AabSelectGroupedDropdown = ({
  value,
  hasLabel,
  focusedGroupIndex,
  focusedOptionIndex,
  isOpen,
  width,
  align,
  groups,
  onMouseOut,
  onOptionClick,
  onOptionMouseOver,
}) => html`
  <div
    tabindex="-1"
    role="listbox"
    aria-labelledby="${hasLabel ? 'aab-select-label' : ''}"
    aria-label="${!hasLabel ? 'Dropdown' : ''}"
    aria-activedescendant="${focusedOptionIndex > -1 && focusedGroupIndex > -1
    ? `aab-option-${focusedGroupIndex}-${focusedOptionIndex}`
    : undefined}"
    class=${`dropdown ${isOpen ? 'd-block' : ''} ${
    align === 'right'
      ? 'dropdown--align-right'
      : 'dropdown--align-left'
  } ${width === 'fixed' ? 'dropdown--width-full' : 'dropdown--width-auto'}`}
    @mouseout=${onMouseOut}
  >
    ${groups.map((group, index) => AabSelectGroup({
    index,
    name: group.name,
    options: group.options,
    value,
    focusedGroupIndex,
    focusedOptionIndex,
    onOptionClick,
    onOptionMouseOver: (optionIndex) => onOptionMouseOver(index, optionIndex),
  }))}
    </div>`;

var css_248z = css`.label ::slotted(label),label{color:#3c5e5e;font-family:Roboto,Helvetica Neue,Arial,sans-serif;font-size:.875rem;font-weight:500;line-height:1.25rem}.sublabel,.text-small{font-size:.875rem;line-height:1.25rem}.sublabel,.text-gray{color:#666!important}.dropdown,.m-0{margin:0!important}.dropdown,.p-0{padding:0!important}.group,.my-0{margin-bottom:0!important;margin-top:0!important}.mt-1{margin-top:8px!important}.mb-1{margin-bottom:8px!important}.px-1,.target{padding-left:8px!important;padding-right:8px!important}.group__label,.option,.px-2{padding-left:16px!important;padding-right:16px!important}.group__label,.option,.py-1{padding-bottom:8px!important;padding-top:8px!important}.group,.pl-0{padding-left:0!important}.option-icon,.smr-1{margin-right:4px!important}.dropdown .smart-search-container,.m-1{margin:8px!important}.position-relative{position:relative!important}.arrow,.dropdown,.position-absolute{position:absolute!important}.d-none,.dropdown{display:none!important}.d-block,.option{display:block!important}.arrow,.d-flex,.target{display:flex!important}.d-inline-flex,.option-icon{display:inline-flex!important}.dropdown ul,.overflow-auto{overflow:auto!important}.align-items-center,.arrow,.target{-ms-flex-align:center!important;align-items:center!important}.arrow,.justify-content-center{-ms-flex-pack:center!important;justify-content:center!important}.target,.text-left{text-align:left!important}.group__label,.option,.target__label,.text-truncate{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.rounded-2,.target{border-radius:2px!important}.dropdown,.shadow-dropdown{box-shadow:0 8px 16px rgba(34,34,34,.3),0 0 8px rgba(34,34,34,.022)!important}.align-bottom,.option-icon{vertical-align:bottom!important}.target{appearance:none;background-color:#fff;border:1px solid #ccc;box-sizing:border-box;color:inherit;cursor:pointer;font-family:inherit;font-size:100%;height:40px;line-height:24px;transition:border-color .2s;width:100%}.target:hover{border-color:#86d1cd}.target:active{border-color:#00857a}.target--info{border-color:#0284c7}.target--success{border-color:#65a30d}.target--warning{border-color:#ea580c}.target--error{border-color:#dc2626}.target--disabled{background-color:#f9f9f9;border-color:#ccc;color:#666;pointer-events:none}.target--no-border{border:none}.target__label{width:calc(100% - 24px)}.target__label--is-placeholder{color:#91aba9}.arrow{height:24px;right:8px;top:8px;transition:transform .2s;width:24px}.arrow--flipped{transform:rotate(180deg)}.dropdown{background-color:#fff;list-style-type:none;outline:none;top:100%;z-index:400}.dropdown--width-full{width:100%}.dropdown--width-auto{min-width:100%}.dropdown--align-left{left:0}.dropdown--align-right{right:0}.dropdown ul{max-height:220px}.group{border-top:1px solid #f0f0f0;list-style-type:none}.group:first-child{border-top:none}.option{background-color:#fff;cursor:pointer;line-height:24px}.option--focused{background-color:#d8f0ef}.option--selected{color:#fff}.option--selected,.option--selected:hover{background-color:#00716b}.option--disabled{color:#ccc;cursor:not-allowed}.option--disabled:hover{background-color:#fff}.dropdown .no-results{color:grey;font-size:16px;line-height:24px;padding:8px 16px}`;

/* eslint-disable lit-a11y/mouse-events-have-key-events */
/* eslint-disable lit-a11y/click-events-have-key-events */

/**
 * @tagname aab-select
 *
 * @prop {string} label - Optional. The sublabel; displays as grey text next to the label
 * @prop {string} sublabel - Optional. The sublabel; displays as grey text next to the label
 * @prop {string} value - Optional. The selected 'value'. Must correspond to the `value` of one of the provided items
 * @prop {object} items - Array of aab-select-items objects
 *  (Option: { name: string, value: string, disabled?: boolean, selectedName?:
 * string, icon?: string, iconType?: string }), strings, or numbers.
 * @prop {object} groups - When passed, groups will take preference over aab-select-items. Array of group objects (`Group: { name: string, options: Array<Option>, }`).
 * @prop {string} variant - Optional. Determines the style (border color) of the select element. Possible values are: 'info', 'success', 'warning', or 'error'.
 * @prop {string} statusMessage - Optional. Replaces the default status message with a custom one.
 * @prop {string} placeholder - Optional. Placeholder you can include in the select. If provided, will display if there is no `value` provided.
 * @prop {boolean} disabled - Optional. Puts the select element in a disabled state.
 * @prop {boolean} noBorder - Optional. Removes the border from select button.
 * @prop {string} dropdownWidth - Optional. Possible values are 'fixed' (the dropdown will have the same width as the AabSelect's button, i.e., `width: 100%`), or 'auto' (default; the width of the dropdown might be larger than the button, and will be determined by its contents, i.e. `min-width: 100%`)
 * @prop {string} alignDropdown - Optional. Determines which side of the dropdown aligns with the button of the AabSelect.' "Only relevant when the dropdown has a different width as the button (i.e., when `align-dropdown` === 'auto'). Can be 'left' (default) or 'right'.
 * @prop {boolean} smartSearch - Optional. Puts the select element with smart search functionality.
 * @prop {string} dropdownDirection - Changes the direction in which the dropdown opens. Possible options: `top`, `bottom` (default).
 */

class AabSelect extends KeyboardDispatcherMixin(
  ScopedElementsMixin(LitElement),
  TranslateMixin,
) {
  static get properties() {
    return {
      value: { type: String, reflect: true, attribute: 'aab-select-value' },
      items: {
        type: Array,
        attribute: 'aab-select-items',
        converter: (value) => convertOptions(JSON.parse(value)),
      },
      groups: {
        type: Array,
        attribute: 'aab-select-groups',
        converter(value) {
          return JSON.parse(value).map((group) => ({
            name: group.name,
            options: convertOptions(group.options),
          }));
        },
      },
      variant: { type: String },
      statusMessage: { type: String, attribute: 'aab-select-status-message' },
      disabled: { type: Boolean, attribute: 'aab-select-disabled' },
      noBorder: { type: Boolean, attribute: 'no-border' },
      placeholder: { type: String, attribute: 'aab-select-placeholder' },
      label: { type: String, attribute: 'aab-select-label' },
      sublabel: { type: String, attribute: 'aab-select-sublabel' },
      dropdownWidth: { type: String, attribute: 'dropdown-width' },
      alignDropdown: { type: String, attribute: 'align-dropdown' },
      isOpen: { type: Boolean, attribute: false },
      focusedOptionIndex: { type: Number, attribute: false },
      focusedGroupIndex: { type: Number, attribute: false },
      rapidlyPressedKeys: { type: String, attribute: false },
      rapidKeyPressTimerId: { type: Number, attribute: false },
      smartSearch: { type: Boolean, attribute: 'aab-smart-search' },
      dropdownDirection: { type: String, attribute: 'dropdown-direction' },
      selectPopper: { type: Object, attribute: false, state: true },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
      'aab-input': AabInput,
      'aab-status': AabStatus,
    };
  }

  static get styles() {
    return [css_248z];
  }

  constructor() {
    super();
    this.items = [];
    this.groups = [];
    this.originalItems = [];
    this.isOpen = false;
    this.focusedOptionIndex = 0;
    this.focusedGroupIndex = 0;
    this.dropdownWidth = 'auto';
    this.alignDropdown = 'left';
    this.rapidlyPressedKeys = '';
    this.rapidKeyPressTimerId = undefined;
    this.currentItem = {};
    this.statusMessage = '';
    this.selectPopper = null;
    this.dropdownDirection = 'bottom';
  }

  connectedCallback() {
    this.originalItems = this.items;
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
  }

  updated() {
    if (this.open && this.selectPopper) {
      this.selectPopper.update();
    }
  }

  get isGroupedSelect() {
    return !!this.groups.length;
  }

  get selectedOption() {
    if (!this.value) {
      return null;
    }

    let selected = null;
    if (this.isGroupedSelect) {
      selected = this.groups.reduce((foundOption, group) => foundOption || group.options.find((option) => option.value === this.value), undefined);
    } else if (this.items) {
      selected = this.items.find(
        (option) => option.value === this.value,
      );
    }
    return selected;
  }

  get focusedOption() {
    let option;
    if (this.isGroupedSelect) {
      option = this.groups[this.focusedGroupIndex].options[this.focusedOptionIndex];
    } else {
      option = this.items[this.focusedOptionIndex];
    }
    return option;
  }

  get selectedOptionIndeces() {
    let selectedGroupIndex = -1;
    let selectedOptionIndex = -1;

    if (this.isGroupedSelect) {
      [selectedGroupIndex, selectedOptionIndex] = this.groups.reduce((result, group, index) => {
        if (result) {
          return result;
        }
        const optionIndex = group.options.findIndex((o) => o.value === this.value);
        return optionIndex > -1 ? [index, optionIndex] : null;
      }, null);
    } else {
      selectedOptionIndex = this.items.findIndex((option) => option.value === this.value);
    }

    return { selectedGroupIndex, selectedOptionIndex };
  }

  get hasAvailableOptions() {
    if (this.isGroupedSelect) {
      return this.groups.some((group) => group.options.some((option) => !option.disabled));
    }
    return this.items.some((option) => !option.disabled);
  }

  displayValue() {
    if (!this.value) {
      return this.placeholder || '-';
    }
    const { selectedOption } = this;
    if (!this.smartSearch && !selectedOption) {
      return 'Invalid value';
    }
    if (this.smartSearch && !selectedOption) {
      return this.currentItem?.name;
    } else {
      this.currentItem = selectedOption;
      return selectedOption.selectedName ?? selectedOption.name;
    }
  }

  handleOptionSelected() {
    const option = this.focusedOption;
    if (option) {
      const changeEvent = new CustomEvent('aab-select-value-change', {
        detail: {
          value: option.value,
        },
        bubbles: true,
      });
      this.dispatchEvent(changeEvent);
      this.toggleDropdown();
    }
  }

  createDropdown() {
    const dropdown = this.shadowRoot.querySelector('.dropdown');
    this.selectPopper = createPopper(this, dropdown, {
      placement: this.dropdownDirection,
      modifiers: [
        {
          name: 'applyStyles',
          enabled: true,
          fn: ({ state }) => {
            Object.assign(state.styles.popper, {
              transform: `translate3d(0px, ${this.dropdownDirection === 'top' ? '-40px' : '40px'}, 0px)`
            });

            Object.assign(state.elements.popper.style, state.styles.popper);
          }
        }
      ],
    });
  }

  async toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const { selectedGroupIndex, selectedOptionIndex } = this.selectedOptionIndeces;
      this.focusedGroupIndex = selectedGroupIndex > -1 ? selectedGroupIndex : 0;
      this.focusedOptionIndex = selectedOptionIndex > -1 ? selectedOptionIndex : 0;

      // Wait for the dropdown to be rendered before we focus it
      await this.updateComplete;
      this.createDropdown();
      const smartSearchElement = this.shadowRoot.querySelector('#smart-search-text');
      if (smartSearchElement) {
        smartSearchElement.value = '';
      }
      this.focusDropdown();
      if (this.smartSearch) {
        this.items = this.originalItems;
        smartSearchElement?.focus();
      }
    } else {
      this.destroyPopper();
      this.focusTarget();
    }
  }

  destroyPopper() {
    this.selectPopper?.destroy();
  }

  focusDropdown() {
    this.shadowRoot.querySelector('.dropdown').focus();
  }

  focusTarget() {
    this.shadowRoot.querySelector('.target').focus();
  }

  /**
   * Keyboard and 'outside click' event handlers
   */
  handleDocumentClick(event) {
    // clickedOutside is defined as 'this' not being in the hierarchy of clicked elements
    const clickedOutside = !event.composedPath().find((element) => element === this);
    if (clickedOutside) {
      if (this.isOpen) {
        this.focusTarget();
      }
      this.isOpen = false;
    }
  }

  handleEnterKey(event) {
    this.handleEnterOrSpaceBar(event);
  }

  handleSpaceBarKey(event) {
    this.handleEnterOrSpaceBar(event);
  }

  handleEnterOrSpaceBar(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else {
      this.handleOptionSelected();
    }
    event.preventDefault();
  }

  handleTabKey(event) {
    if (this.isOpen) {
      event.preventDefault();
    }
  }

  handleEscapeKey(event) {
    this.toggleDropdown();
    event.preventDefault();
  }

  handleHomeKey(event) {
    if (this.isGroupedSelect) {
      const { groupIndex, optionIndex } = findFirstGroupedOption(this.groups);
      this.focusedGroupIndex = groupIndex;
      this.focusedOptionIndex = optionIndex;
    } else {
      const { index } = findFirstOption(this.items);
      this.focusedOptionIndex = index;
    }
    this.scrollFocusedOptionIntoView();
    event.preventDefault();
  }

  handleEndKey(event) {
    if (this.isGroupedSelect) {
      const { groupIndex, optionIndex } = findLastGroupedOption(this.groups);
      this.focusedGroupIndex = groupIndex;
      this.focusedOptionIndex = optionIndex;
    } else {
      const { index } = findLastOption(this.items);
      this.focusedOptionIndex = index;
    }
    this.scrollFocusedOptionIntoView();
    event.preventDefault();
  }

  handleUpArrowKey(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else if (this.hasAvailableOptions) {
      if (this.isGroupedSelect) {
        const { groupIndex, optionIndex } = findPreviousGroupedOption(this.focusedGroupIndex, this.focusedOptionIndex, this.groups);
        this.focusedGroupIndex = groupIndex;
        this.focusedOptionIndex = optionIndex;
      } else {
        const { index } = findPreviousOption(this.focusedOptionIndex, this.items);
        this.focusedOptionIndex = index;
      }
      this.scrollFocusedOptionIntoView();
    }
    event.preventDefault();
  }

  handleDownArrowKey(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else if (this.hasAvailableOptions) {
      if (this.isGroupedSelect) {
        const { groupIndex, optionIndex } = findNextGroupedOption(this.focusedGroupIndex, this.focusedOptionIndex, this.groups);
        this.focusedGroupIndex = groupIndex;
        this.focusedOptionIndex = optionIndex;
      } else {
        const { index } = findNextOption(this.focusedOptionIndex, this.items);
        this.focusedOptionIndex = index;
      }
      this.scrollFocusedOptionIntoView();
    }
    event.preventDefault();
  }

  scrollFocusedOptionIntoView() {
    let option;
    if (this.isGroupedSelect) {
      const group = this.shadowRoot.querySelectorAll('ul')[this.focusedGroupIndex];
      option = group.querySelectorAll('li.option')[this.focusedOptionIndex];
    } else {
      option = this.shadowRoot.querySelectorAll('li.option')[this.focusedOptionIndex];
    }
    option.scrollIntoView({ block: 'nearest' });
  }

  shouldFocusOnCurrentGroupOption(optionIndex, groupIndex) {
    const targetedGroupedOption = this.groups[groupIndex]?.options[optionIndex];
    // Check if the option starts with the keys pressed and is not disabled
    return targetedGroupedOption?.name?.toString().toLowerCase().startsWith(this.rapidlyPressedKeys)
      && !targetedGroupedOption?.disabled;
  }

  shouldFocusOnCurrentOption(optionIndex) {
    const targetedOption = this.items[optionIndex];
    // Check if the option starts with the keys pressed and is not disabled
    return targetedOption?.name?.toLowerCase().startsWith(this.rapidlyPressedKeys)
      && !targetedOption?.disabled;
  }

  findAndScrollToNextGroupedOption() {
    let groupIterationCount = 0;
    // Initializing group index with previously focused group index
    let groupIndex = this.focusedGroupIndex;
    // Initializing option index with next option of previously focused option
    let optionIndex = this.focusedOptionIndex + 1;
    /**
     * Iteration over groups.
     * Running this loop till length as we can have scenario in which the target
       element occurs before the currently focused element in same group.
     */
    do {
      if (groupIndex < 0 || groupIndex > this.groups.length - 1) {
        groupIndex = 0;
        optionIndex = 0;
      }
      // Iteration over options
      do {
        // If option index already exceeds current group's options length then move to next group
        if (optionIndex > this.groups[groupIndex].options.length - 1) {
          break;
        }
        if (this.shouldFocusOnCurrentGroupOption(optionIndex, groupIndex)) {
          this.focusedGroupIndex = groupIndex;
          this.focusedOptionIndex = optionIndex;
          this.scrollFocusedOptionIntoView();
          return;
        }
        optionIndex += 1;
      } while (optionIndex < this.groups[groupIndex].options.length);
      groupIterationCount += 1;
      groupIndex += 1;
      optionIndex = 0;
    } while (groupIterationCount < this.groups.length + 1);
  }

  findAndScrollToNextOption() {
    let optionIndex = this.focusedOptionIndex + 1;
    let iterationCount = 0;
    // Iteration over options
    do {
      if (optionIndex > this.items.length - 1) {
        optionIndex = 0;
      }
      if (this.shouldFocusOnCurrentOption(optionIndex)) {
        this.focusedOptionIndex = optionIndex;
        this.scrollFocusedOptionIntoView();
        return;
      }
      optionIndex += 1;
      iterationCount += 1;
    } while (iterationCount < this.items.length);
  }

  handleRapidKeysPressing() {
    // Clear already running timer
    if (this.rapidKeyPressTimerId) {
      clearTimeout(this.rapidKeyPressTimerId);
    }
    // Starting a new timer to reset keys and timer ID
    this.rapidKeyPressTimerId = setTimeout(() => {
      this.rapidlyPressedKeys = '';
      this.rapidKeyPressTimerId = undefined;
    }, 500);
  }

  onKeyPress(event) {
    // Converting keys to lower case to avoid discrepancy due to casing
    this.rapidlyPressedKeys += event.key.toLowerCase();
    this.handleRapidKeysPressing();
    if (this.isGroupedSelect) {
      this.findAndScrollToNextGroupedOption();
    } else {
      this.findAndScrollToNextOption();
    }
  }
  handleSmartSearch(event) {
    this.items = this.originalItems;
    const result = this.items.filter((item) => {
      if (item.aliases?.length) {
        return item.aliases.some((alias) => alias.toLowerCase()?.startsWith(event.target.value?.toLowerCase()));
      } else {
        return item.value?.toLowerCase().startsWith(event.target.value?.toLowerCase());
      }
    });
    this.items = event.target.value === '' ? this.originalItems : result;
  }

  handleIcon() {
    if (this.smartSearch && !this.selectedOption) {
      return this.currentItem?.icon;
    } else {
      return this.selectedOption?.icon;
    }
  }

  mapVariantToStatusDetails(variant) {
    switch (variant) {
      case 'error':
        return [translate('selectComponentErrorVariant'), 'negative'];
      case 'success':
        return [translate('selectComponentSuccessVariant'), 'positive'];
      case 'warning':
        return [translate('selectComponentWarningVariant'), 'warning'];
      case 'info':
        return [translate('selectComponentInformationVariant'), 'info'];
      default:
        return [];
    }
  }

  renderStatusMessage() {
    return html`<aab-status
      id="aab-select-status"
      class="select-${this.variant}"
      status-message="${this.statusMessage === '' ? this.mapVariantToStatusDetails(this.variant)[0] : this.statusMessage}"
      style-type="${this.mapVariantToStatusDetails(this.variant)[1]}"
      has-icon
    >
    </aab-status>
    `;
  }

  render() {
    const hasLabel = !!this.label || !!this.sublabel;
    return html`
      ${hasLabel ? html`
      <div class="mb-1">
        <label for="aab-select-target" id="aab-select-label">${this.label}</label>
        <span class="sublabel">${this.sublabel}</span>
      </div>` : ''}

      <div class="position-relative" @keypress=${this.onKeyPress}>
      ${AabSelectButton({
    hasLabel,
    isOpen: this.isOpen,
    variant: this.variant,
    disabled: this.disabled,
    noBorder: this.noBorder,
    displayValue: this.displayValue(),
    placeholder: this.placeholder,
    hasValue: !!this.value,
    icon: this.handleIcon(),
    onClick: this.toggleDropdown,
  })}
        ${!this.isGroupedSelect
    ? AabSelectDropdown({
      hasLabel,
      isSmartSearch: this.smartSearch,
      focusedOptionIndex: this.focusedOptionIndex,
      value: this.value,
      isOpen: this.isOpen,
      width: this.dropdownWidth,
      align: this.alignDropdown,
      options: this.items,
      onInputChange: (event) => this.handleSmartSearch(event),
      onMouseOut: () => { this.focusedOptionIndex = -1; },
      onOptionClick: () => { this.handleOptionSelected(); },
      onOptionMouseOver: (index) => { this.focusedOptionIndex = index; },
    }) : AabSelectGroupedDropdown({
      hasLabel,
      focusedGroupIndex: this.focusedGroupIndex,
      focusedOptionIndex: this.focusedOptionIndex,
      value: this.value,
      isOpen: this.isOpen,
      width: this.dropdownWidth,
      align: this.alignDropdown,
      groups: this.groups,
      onMouseOut: () => { this.focusedOptionIndex = -1; this.focusedGroupIndex = -1; },
      onOptionClick: () => { this.handleOptionSelected(); },
      onOptionMouseOver: (groupIndex, optionIndex) => { this.focusedGroupIndex = groupIndex; this.focusedOptionIndex = optionIndex; },
    })
}
  </div>
    ${this.variant && this.variant !== '[none]' ? html`<div class="mt-1">${this.renderStatusMessage()}</div>` : nothing}
`;
  }
}

export { AabSelect as default };
